import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { User, Mail, Phone, FileText, MessageSquare, Calendar, Zap, Shield, BarChart } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface UserDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userEmail: string;
  displayName: string;
  userInfo: any;
  subscriptionInfo: any;
  sessionInfo: any;
  formatDate: (date: string) => string;
}

const UserDetailsModal = ({
  open,
  onOpenChange,
  userId,
  userEmail,
  displayName,
  userInfo,
  subscriptionInfo,
  sessionInfo,
  formatDate
}: UserDetailsModalProps) => {
  const { data: pdfSettings } = useQuery({
    queryKey: ['user-pdf-settings', userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from('pdf_settings')
        .select('lawyer_name, oab_number, oab_state, cpf, whatsapp, include_lawyer_signature')
        .eq('user_id', userId)
        .single();
      
      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar configurações PDF:', error);
      }
      return data;
    },
    enabled: open && !!userId,
  });

  const getInitials = (name: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatTokens = (tokens: number) => {
    if (tokens >= 1000000) {
      return `${(tokens / 1000000).toFixed(1)}M`;
    }
    if (tokens >= 1000) {
      return `${(tokens / 1000).toFixed(1)}K`;
    }
    return tokens.toString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="h-12 w-12">
              <AvatarImage src={userInfo?.avatar_url} />
              <AvatarFallback>
                {getInitials(displayName || userEmail)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="text-xl font-semibold">{displayName || 'Usuário sem nome'}</h2>
              <p className="text-sm text-muted-foreground">{userEmail}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Informações Básicas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Informações Básicas
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium">Status</p>
                  <Badge variant={sessionInfo?.is_online ? "default" : "secondary"}>
                    {sessionInfo?.is_online ? "Online" : "Offline"}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium">Plano</p>
                  <Badge variant="outline">
                    {subscriptionInfo?.plan_type || 'free'}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm font-medium">Último Acesso</p>
                  <p className="text-sm text-muted-foreground">
                    {sessionInfo?.last_active ? formatDate(sessionInfo.last_active) : 'Nunca'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium">Dispositivo</p>
                  <p className="text-sm text-muted-foreground">
                    {sessionInfo?.device_info || 'Desconhecido'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Identificação Profissional */}
          {pdfSettings && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Identificação do Advogado
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 gap-4">
                  {pdfSettings?.lawyer_name && (
                    <div>
                      <p className="text-sm font-medium">Nome Completo</p>
                      <p className="text-sm text-muted-foreground">{pdfSettings?.lawyer_name}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    {pdfSettings?.oab_number && (
                      <div>
                        <p className="text-sm font-medium">OAB</p>
                        <p className="text-sm text-muted-foreground">
                          {pdfSettings?.oab_number} - {pdfSettings?.oab_state}
                        </p>
                      </div>
                    )}
                    {pdfSettings?.cpf && (
                      <div>
                        <p className="text-sm font-medium">CPF</p>
                        <p className="text-sm text-muted-foreground">{pdfSettings?.cpf}</p>
                      </div>
                    )}
                  </div>
                  {pdfSettings?.whatsapp && (
                    <div>
                      <p className="text-sm font-medium flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        WhatsApp
                      </p>
                      <p className="text-sm text-muted-foreground">{pdfSettings?.whatsapp}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Estatísticas de Uso */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart className="h-4 w-4" />
                Estatísticas de Uso
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-4 border rounded-lg">
                  <MessageSquare className="h-6 w-6 mx-auto mb-2 text-blue-500" />
                  <p className="text-2xl font-bold">{userInfo?.conversations_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Conversas</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <FileText className="h-6 w-6 mx-auto mb-2 text-green-500" />
                  <p className="text-2xl font-bold">{userInfo?.documents_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Documentos</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <Zap className="h-6 w-6 mx-auto mb-2 text-yellow-500" />
                  <p className="text-2xl font-bold">
                    {formatTokens(userInfo?.total_tokens || 0)}
                  </p>
                  <p className="text-sm text-muted-foreground">Tokens Usados</p>
                </div>
                <div className="text-center p-4 border rounded-lg">
                  <Calendar className="h-6 w-6 mx-auto mb-2 text-purple-500" />
                  <p className="text-2xl font-bold">
                    {userInfo?.created_at ? formatDate(userInfo.created_at) : 'N/A'}
                  </p>
                  <p className="text-sm text-muted-foreground">Membro desde</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status da Conta */}
          <Card>
            <CardHeader>
              <CardTitle>Status da Conta</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <span>Chat Aprovado</span>
                <Badge variant={userInfo?.status_chat !== false ? "default" : "destructive"}>
                  {userInfo?.status_chat !== false ? "Aprovado" : "Bloqueado"}
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default UserDetailsModal;